import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Switch } from 'components/ui/switch';
import { Slider } from 'components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { 
  ArrowLeft, AlertCircle, MapPin, Clock, DollarSign, User, 
  Bell, CheckCircle, XCircle, Loader2, Zap, Shield, TrendingUp,
  Navigation, Settings, Users, RefreshCw, Star, Phone, PlusCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { Input } from 'components/ui/input';

export default function BackupShifts() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [claimedShifts, setClaimedShifts] = useState([]);
  const [familyRequests, setFamilyRequests] = useState([]);
  const [settings, setSettings] = useState({
    available_for_backup: true,
    radius_miles: 25,
    notifications_enabled: true,
    location: null
  });
  const [stats, setStats] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [manualAddress, setManualAddress] = useState('');
  const [onCallCaregivers, setOnCallCaregivers] = useState([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [selectedProfile, setSelectedProfile] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  
  const isCaregiver = user?.role === 'independent_caregiver';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const requests = [
        api.get(`/backup-caregiver/stats`)
      ];

      if (isCaregiver) {
        requests.push(
          api.get(`/backup-caregiver/available-shifts`),
          api.get(`/backup-caregiver/my-claims`),
          api.get(`/backup-caregiver/settings`)
        );
      } else {
        requests.push(
          api.get(`/backup-caregiver/family/requests`),
          api.get(`/backup-caregiver/on-call-caregivers`),
          api.get(`/care-profiles`)
        );
      }

      const responses = await Promise.all(requests);
      setStats(responses[0].data);

      if (isCaregiver) {
        setAvailableShifts(responses[1].data.shifts || []);
        setClaimedShifts(responses[2].data.claims || []);
        setSettings(responses[3].data.settings || settings);
      } else {
        setFamilyRequests(responses[1].data.requests || []);
        setOnCallCaregivers(responses[2].data.caregivers || []);
        setProfiles(responses[3].data.profiles || []);
      }
    } catch (error) {
      console.error('Failed to fetch backup data:', error);
      toast.error('Failed to load backup shifts');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimShift = async (backupRequestId) => {
    setClaiming(backupRequestId);
    try {
      const response = await api.post(
        `/backup-caregiver/claim/${backupRequestId}`,
        {}
      );
      toast.success(`🎉 ${response.data.message}`, {
        description: `You're now assigned to care for ${response.data.care_recipient}`
      });
      fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Too slow! This shift was claimed by someone else.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to claim shift');
      }
    } finally {
      setClaiming(null);
    }
  };

  const handleRequestCoverage = async () => {
    if (!selectedProfile) {
      toast.error('Please select a care profile');
      return;
    }
    setSubmittingRequest(true);
    try {
      await api.post(`/backup-caregiver/family/request-coverage`, {
        profile_id: selectedProfile,
        reason: requestReason || 'Emergency coverage needed'
      });
      toast.success('Emergency coverage requested! Nearby caregivers have been notified.');
      setRequestDialogOpen(false);
      setRequestReason('');
      setSelectedProfile('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request coverage');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleUpdateSettings = async (newSettings) => {
    setSavingSettings(true);
    try {
      await api.put(
        `/backup-caregiver/settings`,
        newSettings
      );
      setSettings(newSettings);
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported. Please enter your address manually.');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        let address = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const data = await res.json();
          if (data.display_name) {
            const parts = data.address;
            address = [parts?.road, parts?.city || parts?.town || parts?.village, parts?.country]
              .filter(Boolean)
              .join(', ') || data.display_name;
          }
        } catch (_) {}

        const newLocation = { lat: latitude, lng: longitude, address };
        const newSettings = { ...settings, location: newLocation };
        await handleUpdateSettings(newSettings);
        setGettingLocation(false);
        toast.success('Location updated successfully');
      },
      (error) => {
        setGettingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error('Location access denied. Please enter your address manually below.');
        } else {
          toast.error('Unable to get location. Please enter your address manually below.');
        }
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  const handleManualLocation = async () => {
    if (!manualAddress.trim()) {
      toast.error('Please enter an address');
      return;
    }
    const newLocation = {
      lat: 0,
      lng: 0,
      address: manualAddress.trim()
    };
    const newSettings = { ...settings, location: newLocation };
    await handleUpdateSettings(newSettings);
    setManualAddress('');
    toast.success('Location updated successfully');
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'TBD';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-amber-100 text-amber-700 border-amber-200',
      claimed: 'bg-green-100 text-green-700 border-green-200',
      expired: 'bg-slate-100 text-slate-600 border-slate-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200'
    };
    return styles[status] || styles.open;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Backup System</h1>
              <p className="text-xs sm:text-sm text-slate-500 truncate">
                {isCaregiver ? 'Find urgent backup shifts' : 'Track backup requests'}
              </p>
            </div>
          </div>
          <Button onClick={fetchData} variant="outline" size="icon" className="shrink-0 h-8 w-8" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-8 space-y-4 sm:space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {isCaregiver ? (
            <>
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Zap className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{availableShifts.length}</p>
                      <p className="text-sm text-slate-500">Available Now</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats?.total_claims || 0}</p>
                      <p className="text-sm text-slate-500">Shifts Claimed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">1.5x</p>
                      <p className="text-sm text-slate-500">Bonus Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats?.total_requests || 0}</p>
                      <p className="text-sm text-slate-500">Total Requests</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats?.filled || 0}</p>
                      <p className="text-sm text-slate-500">Filled</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats?.open || 0}</p>
                      <p className="text-sm text-slate-500">Still Open</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Main Content */}
        {isCaregiver ? (
          <Tabs defaultValue="available" className="space-y-4">
            <TabsList className="grid w-full grid-cols-3" data-testid="backup-tabs">
              <TabsTrigger value="available" data-testid="available-tab">
                <Zap className="w-4 h-4 mr-2 hidden sm:inline" />
                Available ({availableShifts.length})
              </TabsTrigger>
              <TabsTrigger value="claimed" data-testid="claimed-tab">
                <CheckCircle className="w-4 h-4 mr-2 hidden sm:inline" />
                My Claims ({claimedShifts.length})
              </TabsTrigger>
              <TabsTrigger value="settings" data-testid="settings-tab">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </TabsTrigger>
            </TabsList>

            {/* Available Shifts Tab */}
            <TabsContent value="available" className="space-y-4">
              {availableShifts.length === 0 ? (
                <Card>
                  <CardContent className="p-6 sm:p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <Shield className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No Backup Shifts Available</h3>
                    <p className="text-slate-500 max-w-md mx-auto">
                      All shifts are covered! Check back later or enable notifications to get alerts when backup is needed.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {availableShifts.map((shift) => (
                    <Card key={shift.backup_request_id} className="border-l-4 border-l-amber-500" data-testid="backup-shift-card">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                <Zap className="w-3 h-3 mr-1" />
                                URGENT
                              </Badge>
                              <Badge className="bg-green-100 text-green-700 border-green-200">
                                <DollarSign className="w-3 h-3 mr-1" />
                                1.5x RATE
                              </Badge>
                              {shift.distance_miles && (
                                <Badge variant="outline">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {shift.distance_miles} mi
                                </Badge>
                              )}
                            </div>
                            
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">
                                Care for {shift.care_recipient_name}
                              </h3>
                              <p className="text-sm text-slate-500">
                                Replacing {shift.original_caregiver_name}
                              </p>
                            </div>
                            
                            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4 text-slate-400" />
                                {formatDateTime(shift.scheduled_start)}
                              </div>
                              {shift.location?.address && (
                                <div className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4 text-slate-400" />
                                  {shift.location.address}
                                </div>
                              )}
                            </div>
                            
                            {shift.cancellation_reason && (
                              <p className="text-sm text-slate-500 italic">
                                Reason: {shift.cancellation_reason}
                              </p>
                            )}
                          </div>
                          
                          <Button
                            onClick={() => handleClaimShift(shift.backup_request_id)}
                            disabled={claiming === shift.backup_request_id}
                            className="bg-amber-500 hover:bg-amber-600 text-white min-w-[140px]"
                            data-testid="claim-shift-btn"
                          >
                            {claiming === shift.backup_request_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Zap className="w-4 h-4" />
                            )}
                            Claim Now
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Claimed Shifts Tab */}
            <TabsContent value="claimed" className="space-y-4">
              {claimedShifts.length === 0 ? (
                <Card>
                  <CardContent className="p-6 sm:p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No Claimed Shifts Yet</h3>
                    <p className="text-slate-500">
                      When you claim a backup shift, it will appear here.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {claimedShifts.map((claim) => (
                    <Card key={claim.backup_request_id} data-testid="claimed-shift-card">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={getStatusBadge(claim.status)}>
                                {claim.status.toUpperCase()}
                              </Badge>
                              <Badge className="bg-green-100 text-green-700">
                                1.5x Bonus
                              </Badge>
                            </div>
                            <h3 className="font-semibold text-slate-900">
                              {claim.care_recipient_name}
                            </h3>
                            <p className="text-sm text-slate-500">
                              Claimed on {formatDateTime(claim.claimed_at)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-500">Scheduled</p>
                            <p className="font-medium">{formatDateTime(claim.scheduled_start)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Backup Availability Settings</CardTitle>
                  <CardDescription>Configure when and where you're available for backup shifts</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Available Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Available for Backup</p>
                        <p className="text-sm text-slate-500">Receive alerts for urgent backup shifts</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.available_for_backup}
                      onCheckedChange={(checked) => handleUpdateSettings({ ...settings, available_for_backup: checked })}
                      data-testid="available-toggle"
                    />
                  </div>

                  {/* SMS Notifications Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">SMS Notifications</p>
                        <p className="text-sm text-slate-500">Get text alerts for urgent shifts</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.notifications_enabled}
                      onCheckedChange={(checked) => handleUpdateSettings({ ...settings, notifications_enabled: checked })}
                      data-testid="sms-toggle"
                    />
                  </div>

                  {/* Radius Setting */}
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Navigation className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">Search Radius</p>
                          <p className="text-sm text-slate-500">Only show shifts within this distance</p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-teal-600">{settings.radius_miles} mi</span>
                    </div>
                    <Slider
                      value={[settings.radius_miles]}
                      onValueChange={([value]) => setSettings({ ...settings, radius_miles: value })}
                      onValueCommit={([value]) => handleUpdateSettings({ ...settings, radius_miles: value })}
                      min={5}
                      max={100}
                      step={5}
                      className="w-full"
                      data-testid="radius-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>5 mi</span>
                      <span>50 mi</span>
                      <span>100 mi</span>
                    </div>
                  </div>

                  {/* Location Setting */}
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">Your Location</p>
                          <p className="text-sm text-slate-500">
                            {settings.location ? settings.location.address : 'Set your location for distance-based matching'}
                          </p>
                        </div>
                      </div>
                    </div>
                    {window.isSecureContext && (
                      <Button
                        variant="outline"
                        onClick={getCurrentLocation}
                        disabled={gettingLocation}
                        className="w-full"
                        data-testid="update-location-btn"
                      >
                        {gettingLocation ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Navigation className="w-4 h-4" />
                        )}
                        Use GPS Location
                      </Button>
                    )}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Or enter address manually"
                        value={manualAddress}
                        onChange={(e) => setManualAddress(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleManualLocation()}
                      />
                      <Button
                        variant="outline"
                        onClick={handleManualLocation}
                        disabled={!manualAddress.trim() || savingSettings}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          /* Family View */
          <div className="space-y-6">
            {/* Request Coverage Button */}
            <div className="flex justify-end">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="request-coverage-btn" onClick={() => setRequestDialogOpen(true)}>
                    <PlusCircle className="w-4 h-4" />
                    Request Emergency Coverage
                  </Button>
              <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      Request Emergency Coverage
                    </DialogTitle>
                    <p className="text-sm text-slate-500">Alert nearby on-call caregivers for immediate coverage</p>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Care Recipient</label>
                      <Select value={selectedProfile} onValueChange={setSelectedProfile}>
                        <SelectTrigger data-testid="coverage-profile-select">
                          <SelectValue placeholder="Select care profile" />
                        </SelectTrigger>
                        <SelectContent>
                          {profiles.map(p => (
                            <SelectItem key={p.profile_id} value={p.profile_id}>
                              {p.care_recipient_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Reason</label>
                      <Textarea
                        placeholder="Describe the coverage needed..."
                        value={requestReason}
                        onChange={(e) => setRequestReason(e.target.value)}
                        data-testid="coverage-reason-input"
                      />
                    </div>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">Nearby on-call caregivers will be notified immediately via push + SMS at 1.5x rate.</p>
                    </div>
                    <Button
                      onClick={handleRequestCoverage}
                      disabled={submittingRequest}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="submit-coverage-btn"
                    >
                      {submittingRequest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                      Send Emergency Alert
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* On-Call Caregivers */}
            {onCallCaregivers.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-teal-500" />
                    On-Call Caregivers ({onCallCaregivers.length})
                  </CardTitle>
                  <CardDescription>Available caregivers ready for emergency backup at 1.5x rate</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {onCallCaregivers.slice(0, 6).map((cg) => (
                      <div key={cg.user_id} className="p-4 border rounded-lg hover:bg-slate-50 transition-colors" data-testid="on-call-card">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold">
                            {cg.name?.[0] || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{cg.name}</p>
                            <div className="flex items-center gap-2">
                              {cg.rating > 0 && (
                                <span className="flex items-center gap-1 text-xs text-amber-600">
                                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                  {cg.rating?.toFixed(1)}
                                </span>
                              )}
                              {cg.hourly_rate && (
                                <span className="text-xs text-slate-500">${cg.hourly_rate}/hr</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {cg.services?.slice(0, 2).map(s => (
                            <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                          ))}
                          {cg.total_hours > 0 && (
                            <Badge variant="outline" className="text-xs">{Number(cg.total_hours).toFixed(1)}h worked</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Backup Requests History */}
            <Card>
            <CardHeader>
              <CardTitle>Backup Requests History</CardTitle>
              <CardDescription>Track cancelled shifts and backup replacements</CardDescription>
            </CardHeader>
            <CardContent>
              {familyRequests.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Backup Requests</h3>
                  <p className="text-slate-500">
                    When a caregiver cancels a shift, backup requests will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {familyRequests.map((req) => (
                    <div key={req.backup_request_id} className="p-4 border rounded-lg" data-testid="family-request-card">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className={getStatusBadge(req.status)}>
                          {req.status === 'claimed' ? '✅ FILLED' : req.status.toUpperCase()}
                        </Badge>
                        <span className="text-sm text-slate-500">
                          {formatDateTime(req.created_at)}
                        </span>
                      </div>
                      <h4 className="font-medium text-slate-900 mb-1">
                        Care for {req.care_recipient_name}
                      </h4>
                      <p className="text-sm text-slate-500 mb-2">
                        Original: {req.original_caregiver_name}
                        {req.claimed_by_name && (
                          <span className="text-green-600"> → Replacement: {req.claimed_by_name}</span>
                        )}
                      </p>
                      {req.cancellation_reason && (
                        <p className="text-sm text-slate-400 italic">
                          Reason: {req.cancellation_reason}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        )}

        {/* How It Works */}
        <Card>
          <CardHeader>
            <CardTitle>How Instant Backup Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">1. Caregiver Cancels</h4>
                <p className="text-sm text-slate-500">
                  When a caregiver can't make their shift, they cancel through the app
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                  <Bell className="w-6 h-6 text-amber-600" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">2. Instant Alerts</h4>
                <p className="text-sm text-slate-500">
                  Nearby available caregivers receive push & SMS notifications immediately
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">3. First Wins</h4>
                <p className="text-sm text-slate-500">
                  First caregiver to claim gets the job at 1.5x rate. Family gets instant coverage!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
